import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Props {
  orgId: string;
  logoUrl: string | null;
  canEdit: boolean;
  onUpdated: (newUrl: string) => void;
}

export default function LogoUploader({ orgId, logoUrl, canEdit, onUpdated }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Max 5 MB.');
      return;
    }

    setUploading(true);
    setError('');

    const ext = file.name.split('.').pop() ?? 'png';
    const path = `${orgId}/logo_${Date.now()}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from('org-logos')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadErr) {
      setError(uploadErr.message);
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('org-logos').getPublicUrl(path);
    const publicUrl = urlData.publicUrl;

    const { error: updateErr } = await supabase
      .from('organizations')
      .update({ logo_url: publicUrl, updated_at: new Date().toISOString() })
      .eq('id', orgId);

    if (updateErr) {
      setError(updateErr.message);
    } else {
      onUpdated(publicUrl);
    }

    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="relative flex-shrink-0">
      <div
        className={`w-14 h-14 flex items-center justify-center bg-[#1a2e4a]/8 rounded-xl overflow-hidden ${canEdit ? 'cursor-pointer group' : ''}`}
        onClick={() => canEdit && inputRef.current?.click()}
        title={canEdit ? 'Click to change logo' : undefined}
      >
        {uploading ? (
          <i className="ri-loader-4-line text-[#1a2e4a] text-xl animate-spin" />
        ) : logoUrl ? (
          <>
            <img src={logoUrl} alt="Logo" className="w-10 h-10 object-contain" />
            {canEdit && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                <i className="ri-camera-line text-white text-lg" />
              </div>
            )}
          </>
        ) : (
          <>
            <i className="ri-building-2-line text-[#1a2e4a] text-2xl" />
            {canEdit && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                <i className="ri-camera-line text-white text-lg" />
              </div>
            )}
          </>
        )}
      </div>
      {canEdit && (
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/svg+xml"
          className="hidden"
          onChange={handleFileChange}
        />
      )}
      {error && (
        <p className="absolute top-full left-0 mt-1 text-xs text-red-500 whitespace-nowrap bg-white px-2 py-1 rounded shadow-sm border border-red-100 z-10">
          {error}
        </p>
      )}
    </div>
  );
}
